// exports: formatCandles, getCachedCandles, collectCachedUpdates,
//          getProvider, fetchAlpacaCandles
import axios from "axios";
import { enqueue } from "./requestQueue";

// Retries a request on HTTP 429 (rate limited) with a short backoff, so a transient
// rate-limit hit on the DISPLAY charts' own fetch self-heals instead of failing outright.
// Any other error (network, 4xx/5xx besides 429) propagates immediately — this is only a
// stopgap for the rate-limit symptom, not a general retry-everything wrapper. See
// docs/known-issue-rate-limiting-blank-charts.md for the full root-cause writeup; the
// actual fix (a shared rate limiter across all pollers) is deferred to a later build.
async function withRateLimitRetry(requestFn, retries = 2, baseDelayMs = 1200) {
  let attempt = 0;
  for (;;) {
    try {
      return await requestFn();
    } catch (err) {
      const status = err?.response?.status;
      if (status !== 429 || attempt >= retries) throw err;
      const retryAfterHeader = err?.response?.headers?.["retry-after"];
      const retryAfterMs = retryAfterHeader ? Number(retryAfterHeader) * 1000 : null;
      const delay = Number.isFinite(retryAfterMs) ? retryAfterMs : baseDelayMs * (attempt + 1);
      await new Promise((resolve) => setTimeout(resolve, delay));
      attempt += 1;
    }
  }
}

export const formatCandles = (raw) => {
    return raw.map(c => ({
      time:   c[0],
      open:   c[3],
      high:   c[2],
      low:    c[1],
      close:  c[4],
      volume: c[5]
    })).reverse();
  };
  
  export function getCachedCandles(allCandles, coin, timeframe) {
    return (allCandles[coin] && allCandles[coin][timeframe]) || [];
  }
  
  export function collectCachedUpdates(followedCoins, timeframes, allCandles) {
    const cache = {};
    for (const coin of Object.keys(followedCoins)) {
      if (!followedCoins[coin]) continue;
      cache[coin] = {};
      for (const tf of timeframes) {
        cache[coin][tf] = getCachedCandles(allCandles, coin, tf);
      }
    }
    return cache;
  }

// ───────────────────────────────────────────────────────────────
// Source routing (Phase 5 Build 1)
// ───────────────────────────────────────────────────────────────

// One classifier, one place. Crypto symbols are dashed pairs (e.g. "BTC-USD")
// -> Coinbase. Bare tickers (e.g. "AAPL") -> Alpaca stock market data.
export function getProvider(symbol) {
  return symbol.includes("-") ? "coinbase" : "alpaca";
}

// Coinbase's public candles endpoint hard-caps at 300 candles per request and
// REJECTS (not truncates) any start/end + granularity combo that implies more —
// confirmed against the current docs. To get more history than a single 300-candle
// page, page backwards in exact 300-candle windows and concatenate.
//
// Number of pages to fetch per granularity (a page = up to 300 candles). Chosen to
// give meaningfully more chart history than the old single-page (300 candle) default
// while keeping this to a few requests — this is a DISPLAY-chart helper only (called
// once per chart open), not part of the scoring engine's own candle fetches.
const COINBASE_PAGES = {
  60: 2,      // 1m  -> ~10 hours
  300: 2,     // 5m  -> ~2 days
  900: 3,     // 15m -> ~9 days
  3600: 4,    // 1h  -> ~50 days
  21600: 4,   // 6h  -> ~300 days
  86400: 3,   // 1d  -> ~2.5 years
};

// Fetches up to COINBASE_PAGES[granularity] pages of candles (newest-first, same
// raw positional shape Coinbase always returns — [time,low,high,open,close,volume]),
// concatenated oldest-page-last. Stops early if a page comes back short (reached the
// start of available history). Safe to feed straight into normalizeCandles/formatCandles
// exactly like a single-page response was before.
export async function fetchCoinbaseCandles(symbol, granularity, priority = false) {
  const pages = COINBASE_PAGES[granularity] || 1;
  const pageSpanSec = 300 * granularity;
  // PARALLEL pages (display-latency fix): windows are deterministic — each page spans a
  // fixed 300-candle time slice stepping back from "now" — so all pages fire through the
  // queue AT ONCE (starts still spaced by the queue) instead of chaining request → response
  // → next request. 4 sequential round-trips (the "6h takes 3-5s" lag) become ~1. Page 0
  // keeps Coinbase's default "ending now" (no params) — byte-identical to the old first
  // request; deeper pages step back one candle between windows to avoid overlap, exactly
  // like the old loop's end = oldest - granularity. A thin/short window just returns fewer
  // (or zero) candles and concat skips nothing — sparse coins simply render what exists.
  const anchorSec = Math.floor(Date.now() / 1000);
  const requests = [];
  for (let i = 0; i < pages; i++) {
    const params = { granularity };
    if (i > 0) {
      const end = anchorSec - i * pageSpanSec - i * granularity; // window top, one-candle gap per step
      params.end = new Date(end * 1000).toISOString();
      params.start = new Date((end - pageSpanSec) * 1000).toISOString();
    }
    requests.push(withRateLimitRetry(() => enqueue("coinbase-exchange", () => axios.get(
      `https://api.exchange.coinbase.com/products/${symbol}/candles`,
      { params }
    ), { priority })));
  }
  const results = await Promise.all(requests);
  // Concat newest page first (same order the old loop built), deduping on timestamp:
  // a window-seam overlap would otherwise produce duplicate candle times, which
  // lightweight-charts setData() hard-rejects. First occurrence (newer page) wins.
  const seen = new Set();
  const all = [];
  for (const res of results) {
    for (const c of res.data || []) {
      if (!seen.has(c[0])) { seen.add(c[0]); all.push(c); }
    }
  }
  return all;
}

// Coinbase granularity (seconds) -> Alpaca bar timeframe. Exact 1:1 map; every
// Coinbase granularity has a real Alpaca equivalent (Hour unit allows 1-23, so
// 6Hour is valid). No nearest-neighbor guessing — an unmapped granularity throws.
const ALPACA_TIMEFRAME = {
  60: "1Min",
  300: "5Min",
  900: "15Min",
  3600: "1Hour",
  21600: "6Hour",
  86400: "1Day",
};

// Per-timeframe history window (days) for no-limit depth callers, so each
// timeframe clears the >=50-bar gate. Keyed by the same granularity values as
// ALPACA_TIMEFRAME. Only used when no explicit `limit` is passed (recent-N
// callers like limit:2 keep Alpaca's default ~1-day window — no start).
const ALPACA_LOOKBACK_DAYS = {
  60: 3,
  300: 3,
  900: 5,
  3600: 15,
  21600: 120,
  86400: 90,
};

// Bigger lookback for the two DISPLAY charts only (SignalChart.js, More Info in App.js) —
// passed explicitly as fetchAlpacaCandles' 4th param by those two call sites. Every other
// caller (the scoring/coin-update loops) keeps calling fetchAlpacaCandles with just 2 args,
// so this override never touches their candle depth or indicator math. Mirrors the same
// display-only-extension pattern used for Coinbase (candles.js COINBASE_PAGES).
export const ALPACA_DISPLAY_LOOKBACK_DAYS = {
  3600: 60,   // 1h -> was 15 days shared-default
  21600: 240, // 6h -> was 120
  86400: 365, // 1d -> was 90
};

// Fetch Alpaca bars for a stock symbol and return them in the SAME shape the
// Coinbase candle fetch yields (res.data): an array of positional arrays,
// pre-normalize, so formatCandles(normalizeCandles(...)) consumes it unchanged.
//
// Each Alpaca bar {t,o,h,l,c,v,...} (oldest-first, t = RFC3339 string) maps to
// the Coinbase positional shape [time, low, high, open, close, volume]:
//   h -> [2], l -> [1], drop n and vw.
// time is emitted as NUMERIC epoch-seconds (Math.floor(Date.parse(t)/1000));
// normalizeCandles only flips oldest->newest when timestamps are numbers, so a
// string here would leave the bars reversed downstream. We do NOT reverse here
// — normalizeCandles owns the flip.
export async function fetchAlpacaCandles(symbol, granularity, limit, lookbackDaysOverride, priority = false) {
  const timeframe = ALPACA_TIMEFRAME[granularity];
  if (!timeframe) {
    throw new Error(
      `fetchAlpacaCandles: no Alpaca timeframe for Coinbase granularity ${granularity}`
    );
  }
  const endpoint = process.env.REACT_APP_ALPACA_ENDPOINT;
  // feed=iex — this is a free Alpaca account (IEX only). With no explicit feed the request
  // resolves to the default (SIP) feed, which the free plan may not query: Alpaca returns 403
  // (or silently truncates) on the recent end of a window, so the "More Info" chart was
  // fetching stale/incomplete history. Mirrors the same fix already applied to the worker's
  // fetchSources.cjs (fetchAlpacaCandles / fetchAlpacaCandlesBatch).
  const params = { timeframe, feed: "iex" };
  if (limit != null) {
    params.limit = limit;                       // recent-N callers (e.g. limit:2) keep current behavior — no start
  } else {
    const days = lookbackDaysOverride ?? ALPACA_LOOKBACK_DAYS[granularity];
    if (days != null) {
      params.start = new Date(Date.now() - days * 86400000).toISOString();
    }
    // Max page size for window (start-based) fetches. Alpaca pages long windows in small
    // chunks by default (observed ~55-70 bars/page on 6Hour), turning a 240-day 6h chart
    // into 8+ sequential round-trips — the "6h takes 3-5s" lag. limit here is PAGE SIZE,
    // not a data cap: the window is still bounded by `start`, the pagination loop still
    // follows next_page_token if one ever appears, and the returned bars are identical.
    params.limit = 10000;
  }
  // Paginate. Confirmed live (network capture) that Alpaca's single-symbol bars endpoint
  // pages long windows — the 6Hour/120-day lookback was cutting off after ~55-70 bars
  // (next_page_token present, never followed), so "More Info" charts for larger timeframes
  // silently stopped around 5-6 weeks in instead of reaching "now". Mirrors the pagination
  // loop already proven in the worker's fetchAlpacaCandlesBatch (fetchSources.cjs). Only
  // loops for start-based (lookback) calls — limit-based recent-N callers (e.g. limit:2)
  // stay single-page, matching existing behavior.
  let bars = [];
  let pageToken = null;
  let pageCount = 0;
  const MAX_PAGES = 20; // safety cap — a real window should never need this many pages
  do {
    if (pageToken) params.page_token = pageToken;
    const res = await withRateLimitRetry(() => enqueue("alpaca", () => axios.get(`${endpoint}/v2/stocks/${symbol}/bars`, {
      params,
      headers: {
        "APCA-API-KEY-ID": process.env.REACT_APP_ALPACA_KEY,
        "APCA-API-SECRET-KEY": process.env.REACT_APP_ALPACA_SECRET,
      },
    }), { priority }));
    bars = bars.concat((res.data && res.data.bars) || []);
    pageToken = res.data && res.data.next_page_token;
    pageCount += 1;
  } while (pageToken && limit == null && pageCount < MAX_PAGES);
  return bars.map((b) => [
    Math.floor(Date.parse(b.t) / 1000), // [0] time  (numeric epoch seconds)
    b.l,                                // [1] low
    b.h,                                // [2] high
    b.o,                                // [3] open
    b.c,                                // [4] close
    b.v,                                // [5] volume
  ]);
}

// Batched latest price for a list of bare stock tickers. One request for all
// symbols via Alpaca's multi-symbol latest-trade endpoint, same auth as
// fetchAlpacaCandles. Returns a plain { SYMBOL: number } map of last trade
// price (data.trades[SYM].p); symbols absent from the response are omitted.
export async function fetchAlpacaLatestPrices(symbols) {
  const endpoint = process.env.REACT_APP_ALPACA_ENDPOINT;
  const res = await enqueue("alpaca", () => axios.get(`${endpoint}/v2/stocks/trades/latest`, {
    params: { symbols: symbols.join(",") },
    headers: {
      "APCA-API-KEY-ID": process.env.REACT_APP_ALPACA_KEY,
      "APCA-API-SECRET-KEY": process.env.REACT_APP_ALPACA_SECRET,
    },
  }));
  const trades = (res.data && res.data.trades) || {};
  const prices = {};
  for (const [sym, trade] of Object.entries(trades)) {
    if (trade && trade.p != null) prices[sym] = trade.p;
  }
  return prices;
}

// Batched snapshots for a list of bare stock tickers. One request via Alpaca's
// multi-symbol snapshots endpoint, same auth as the other helpers. Returns a
// { SYMBOL: { price, prevClose } } map where price = latestTrade.p and
// prevClose = prevDailyBar.c (previous trading-day close — the correct daily-
// change reference). Symbols missing either value are omitted from the map.
export async function fetchAlpacaSnapshots(symbols) {
  const endpoint = process.env.REACT_APP_ALPACA_ENDPOINT;
  const res = await enqueue("alpaca", () => axios.get(`${endpoint}/v2/stocks/snapshots`, {
    params: { symbols: symbols.join(",") },
    headers: {
      "APCA-API-KEY-ID": process.env.REACT_APP_ALPACA_KEY,
      "APCA-API-SECRET-KEY": process.env.REACT_APP_ALPACA_SECRET,
    },
  }));
  const snaps = res.data || {};
  const out = {};
  for (const [sym, snap] of Object.entries(snaps)) {
    const price = snap && snap.latestTrade && snap.latestTrade.p;
    const prevClose = snap && snap.prevDailyBar && snap.prevDailyBar.c;
    if (price != null && prevClose != null) out[sym] = { price, prevClose };
  }
  return out;
}